import React from "react";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Today } from "@mui/icons-material";
import dayjs from "dayjs";
import { formatDisplayDate } from "./dateFormat";

export const getPeriodRange = (viewMode, anchorDate) => {
  const anchor = dayjs(anchorDate || undefined);
  if (viewMode === "week") {
    return {
      startDate: anchor.startOf("week").format("YYYY-MM-DD"),
      endDate: anchor.endOf("week").format("YYYY-MM-DD"),
      anchorDate: anchor.format("YYYY-MM-DD"),
    };
  }
  return {
    startDate: anchor.startOf("month").format("YYYY-MM-DD"),
    endDate: anchor.endOf("month").format("YYYY-MM-DD"),
    anchorDate: anchor.startOf("month").format("YYYY-MM-DD"),
  };
};

export const getPeriodTitle = (viewMode, startDate, endDate) => {
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  if (viewMode === "month") {
    return start.format("MMMM YYYY");
  }
  if (start.isSame(end, "month") && start.isSame(end, "year")) {
    return `${start.format("DD.")} – ${formatDisplayDate(endDate)}`;
  }
  return `${formatDisplayDate(startDate)} – ${formatDisplayDate(endDate)}`;
};

export const isViewingCurrentPeriod = (viewMode, startDate, endDate) => {
  if (!startDate || !endDate) return true;
  const today = dayjs();
  if (viewMode === "month") {
    return today.isSame(dayjs(startDate), "month");
  }
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  return !today.isBefore(start, "day") && !today.isAfter(end, "day");
};

const navIconSx = {
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
  "&:hover": { bgcolor: "grey.50" },
};

const PeriodNavigator = ({ viewMode, startDate, endDate, onViewModeChange, onNavigate, onToday, loading }) => {
  const title = getPeriodTitle(viewMode, startDate, endDate);
  const onCurrentPeriod = isViewingCurrentPeriod(viewMode, startDate, endDate);
  const todayTooltip =
    viewMode === "week" ? "Go to current week" : "Go to current month";

  return (
    <Box sx={{ mb: 2 }}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr auto 1fr" },
          alignItems: "center",
          gap: { xs: 1.5, sm: 2 },
          py: 1,
        }}
      >
        <Box sx={{ justifySelf: { xs: "center", sm: "start" } }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            size="small"
            onChange={(_, value) => value && onViewModeChange(value)}
            disabled={loading}
            sx={{
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              "& .MuiToggleButton-root": {
                px: 2.5,
                py: 0.75,
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.875rem",
                border: 0,
                borderRadius: "8px !important",
                color: "text.secondary",
                "&.Mui-selected": {
                  bgcolor: "primary.main",
                  color: "primary.contrastText",
                  "&:hover": { bgcolor: "primary.dark" },
                },
              },
            }}
          >
            <ToggleButton value="week">Week</ToggleButton>
            <ToggleButton value="month">Month</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box
          sx={{
            justifySelf: "center",
            textAlign: "center",
            px: 1,
            minWidth: { xs: 180, sm: 220 },
          }}
        >
          <Typography variant="h6" fontWeight={700} lineHeight={1.2} noWrap>
            {title}
          </Typography>
        </Box>

        <Box
          sx={{
            justifySelf: { xs: "center", sm: "end" },
            display: "flex",
            alignItems: "center",
            gap: 0.5,
          }}
        >
          <Tooltip title={viewMode === "week" ? "Previous week" : "Previous month"}>
            <span>
              <IconButton
                size="small"
                onClick={() => onNavigate(-1)}
                disabled={loading}
                sx={navIconSx}
                aria-label={viewMode === "week" ? "Previous week" : "Previous month"}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={viewMode === "week" ? "Next week" : "Next month"}>
            <span>
              <IconButton
                size="small"
                onClick={() => onNavigate(1)}
                disabled={loading}
                sx={navIconSx}
                aria-label={viewMode === "week" ? "Next week" : "Next month"}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={onCurrentPeriod ? "Already viewing current period" : todayTooltip}>
            <span>
              <Button
                size="small"
                variant={onCurrentPeriod ? "outlined" : "contained"}
                startIcon={<Today fontSize="small" />}
                onClick={onToday}
                disabled={loading || onCurrentPeriod}
                sx={{
                  ml: 0.5,
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: 88,
                  boxShadow: onCurrentPeriod ? "none" : undefined,
                }}
              >
                Today
              </Button>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {loading && (
        <LinearProgress sx={{ mt: 0.5, borderRadius: 1, height: 2 }} />
      )}
    </Box>
  );
};

export default PeriodNavigator;
